import { useState } from 'react'
import { Logo } from '../components/Logo'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useSession } from '../auth/session'
import { ApiError } from '../lib/api'

const schema = z.object({
  email: z.string().min(1, 'Enter your email address').email('That does not look like an email address'),
  password: z.string().min(1, 'Enter your password'),
})

type Values = z.infer<typeof schema>

export function SignIn() {
  const signIn = useSession((s) => s.signIn)
  const [error, setError] = useState<string | null>(null)
  // The password was right and a code comes next (D-127).
  const [challenge, setChallenge] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({ resolver: zodResolver(schema) })

  const onSubmit = async (values: Values) => {
    setError(null)
    try {
      const next = await signIn(values.email, values.password)
      if (next) setChallenge(next.challenge)
    } catch (err) {
      // The server answers identically for a wrong password and an unknown
      // account, deliberately — repeating its message rather than inventing a
      // more specific one keeps that property intact.
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many attempts. Wait a minute and try again.'
          : 'The email address or password is incorrect.',
      )
    }
  }

  if (challenge) return <CodeStep challenge={challenge} onRestart={() => setChallenge(null)} />

  return (
    <div className="flex min-h-screen items-center justify-center bg-dpl-bg px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-1.5">
          <Logo size={52} tone="dark" />
          <p className="text-xs text-dpl-muted">Business Management Platform</p>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="rounded-lg border border-dpl-border bg-dpl-surface p-6 shadow-sm"
          noValidate
        >
          <h1 className="mb-5 text-base font-semibold text-dpl-text">Sign in</h1>

          {error && (
            <p
              role="alert"
              className="mb-4 rounded border border-dpl-danger/30 bg-dpl-danger/5 px-3 py-2 text-sm text-dpl-danger"
            >
              {error}
            </p>
          )}

          <Field label="Email address" error={errors.email?.message}>
            <input
              {...register('email')}
              type="email"
              autoComplete="username"
              autoFocus
              className={inputClass(!!errors.email)}
            />
          </Field>

          <Field label="Password" error={errors.password?.message}>
            <input
              {...register('password')}
              type="password"
              autoComplete="current-password"
              className={inputClass(!!errors.password)}
            />
          </Field>

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-2 w-full rounded-md bg-dpl-primary py-2.5 text-sm font-medium text-white transition hover:bg-dpl-primary-hover disabled:opacity-60"
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>

          <p className="mt-4 text-center text-xs">
            <a href="/forgot-password" className="text-dpl-primary hover:underline">
              Forgot your password?
            </a>
          </p>
        </form>
      </div>
    </div>
  )
}

/** The second step (D-127): the six digits the authenticator app shows, or a recovery code. */
function CodeStep({ challenge, onRestart }: { challenge: string; onRestart: () => void }) {
  const completeTwoFactor = useSession((s) => s.completeTwoFactor)
  const [recovery, setRecovery] = useState(false)
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await completeTwoFactor(challenge, recovery ? { recoveryCode: value } : { code: value })
    } catch (err) {
      setBusy(false)
      if (err instanceof ApiError && err.status === 429) setError('Too many attempts. Wait a minute and try again.')
      else if (err instanceof ApiError && err.detail?.includes('expired')) { onRestart(); return }
      else setError(recovery ? 'That recovery code is not right, or was used already.' : 'That code is not right, or was used already. Enter the one the app shows now.')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-dpl-bg px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-1.5">
          <Logo size={52} tone="dark" />
          <p className="text-xs text-dpl-muted">Business Management Platform</p>
        </div>
        <form onSubmit={submit} className="rounded-lg border border-dpl-border bg-dpl-surface p-6 shadow-sm" noValidate>
          <h1 className="mb-1 text-base font-semibold text-dpl-text">Two-factor sign-in</h1>
          <p className="mb-5 text-sm text-dpl-muted">
            {recovery ? 'Enter one of the recovery codes you saved. Each works once.' : 'Enter the 6-digit code from your authenticator app (Google Authenticator).'}
          </p>
          {error && <p role="alert" className="mb-4 rounded border border-dpl-danger/30 bg-dpl-danger/5 px-3 py-2 text-sm text-dpl-danger">{error}</p>}
          <Field label={recovery ? 'Recovery code' : 'Code'}>
            <input
              key={recovery ? 'recovery' : 'code'}
              autoFocus
              autoComplete="one-time-code"
              inputMode={recovery ? 'text' : 'numeric'}
              placeholder={recovery ? 'ABCD-EFGH' : '123 456'}
              maxLength={recovery ? 12 : 7}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className={`${inputClass(false)} tracking-widest`}
            />
          </Field>
          <button type="submit" disabled={busy || value.replace(/\s/g, '').length < (recovery ? 8 : 6)}
            className="mt-2 w-full rounded-md bg-dpl-primary py-2.5 text-sm font-medium text-white transition hover:bg-dpl-primary-hover disabled:opacity-60">
            {busy ? 'Checking…' : 'Sign in'}
          </button>
          <div className="mt-4 flex justify-between text-xs">
            <button type="button" className="text-dpl-primary hover:underline" onClick={() => { setRecovery(!recovery); setValue(''); setError(null) }}>
              {recovery ? 'Use the app instead' : 'Lost your phone? Use a recovery code'}
            </button>
            <button type="button" className="text-dpl-muted hover:underline" onClick={onRestart}>Start again</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <label className="mb-4 block">
      <span className="mb-1 block text-xs font-medium text-dpl-muted">{label}</span>
      {children}
      {error && <span className="mt-1 block text-xs text-dpl-danger">{error}</span>}
    </label>
  )
}

export function inputClass(hasError: boolean): string {
  return [
    'w-full rounded-md border bg-dpl-surface px-3 py-2 text-base text-dpl-text sm:text-sm',
    'focus:outline-none focus:ring-1',
    hasError
      ? 'border-dpl-danger focus:border-dpl-danger focus:ring-dpl-danger'
      : 'border-dpl-border focus:border-dpl-primary focus:ring-dpl-primary',
  ].join(' ')
}
